import { FormItemType } from '../../UI/view/component-map';
import { ListView, ListViewColumnType, initListView } from '../../UI/view/list-view';
import { DataSource } from '../../UI/view/data-source';
import {
  getOrderManifests,
  addOrderManifest,
  updateOrderManifest,
  deleteOrderManifest,
} from '../../../services/requests/orders/orders-requests';
import { GetDewarProductTypeList } from '../../../services/requests/billing/dewar-billing-requests';
import { ordersActionPermissions } from './orders-datasource';

// Legacy "Service Charge" product (odsDewarItemsNoSC excludes id 110 from the
// ADD dropdown; the EDIT dropdown keeps all products).
const SERVICE_CHARGE_PRODUCT_ID = 110;

// Currency formatter mirroring legacy {0:C} (2 decimals) with thousands separators.
const money = (v: any) => {
  const n = Number(v);
  return Number.isFinite(n)
    ? `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : '';
};

// Product Manifest grid (legacy divProductManifest / dbo.OrderManifest), Dewar-
// only, as a reusable CRUD listview. Add/edit/delete persist immediately via the
// dedicated manifest endpoints; the server recomputes fees + product unit price
// (ComputeProductUnitPriceAsync) so no client pricing is needed. Scoped to the
// loaded order via requestArgs; new rows get orderId via addTemplate.
export const orderManifestsDataSource = {
  name: 'orderManifests',
  pk: 'id',
  request: getOrderManifests,
  requestArgs: (state: any) => state.orders?.current?.data?.id,
  createRequest: addOrderManifest,
  updateRequest: updateOrderManifest,
  deleteRequest: deleteOrderManifest,
} as DataSource;

const manifestProductOptions = async () => {
  const list = await GetDewarProductTypeList();
  return [
    { id: '', text: '-- Select One --' },
    ...(list ?? [])
      .filter((p: any) => p.isActive !== false)
      .map((p: any) => ({
        id: p.id,
        // Legacy odsDewarItems display: "{ProductNumber} - {Description}".
        text: p.description ? `${p.productNumber} - ${p.description}` : p.productNumber,
      })),
  ];
};

// Add dropdown excludes the Service Charge product (legacy NoSC list); the edit
// dropdown keeps all products.
const manifestProductOptionsNoSC = async () => {
  const options = await manifestProductOptions();
  return options.filter((o: any) => Number(o.id) !== SERVICE_CHARGE_PRODUCT_ID);
};

const manifestFields = (productOptions: () => Promise<any[]>) => [
  { name: 'productTypeId', label: 'Product', type: FormItemType.select, searchable: true, required: true, options: productOptions },
  { name: 'qtyShipped', label: 'Qty Ship', type: FormItemType.text, number: true, defaultValue: 0 },
  { name: 'qtyReturned', label: 'Qty Return', type: FormItemType.text, number: true, defaultValue: 0 },
  { name: 'unitPrice', label: 'Unit Price', type: FormItemType.text, number: true },
  { name: 'description', label: 'Description', type: FormItemType.text },
];

// The parent orderId is seeded onto new rows via addTemplate and flows through
// handleSubmit (undeclared fields are preserved); on edit the row (currentEntity)
// already carries id + orderId.
export const orderManifestCreateForm = {
  permissions: ordersActionPermissions,
  addTemplate: (state: any) => ({ orderId: state.orders?.current?.data?.id, qtyShipped: 0, qtyReturned: 0 }),
  items: manifestFields(manifestProductOptionsNoSC),
};

export const orderManifestUpdateForm = {
  permissions: ordersActionPermissions,
  items: manifestFields(manifestProductOptions),
};

const manifestLineTotal = (row: any) => (Number(row?.qtyShipped) || 0) * (Number(row?.unitPrice) || 0);

// Legacy (HeliumOrders.aspx.cs btnAddManifestItem_Click / btnEditDewarManifestSave_Click)
// blocks a manifest line only when qtyShip + qtyRet == 0 (a zero in one field is
// allowed). The check is unconditional for every product. Applied to the Qty Ship
// cell so the row is flagged when both quantities are zero.
const positiveQtyValidation = {
  positiveQty: {
    message: 'Please enter a positive value for qty ship or qty return.',
    handler: (_val: any, getValues: any, _existing: any, row: number) => {
      const line = (getValues('manifests') || [])[row];
      if (!line) return true;
      return (Number(line.qtyShipped) || 0) + (Number(line.qtyReturned) || 0) > 0;
    },
  },
};

export const orderManifestsListView = {
  permissions: ordersActionPermissions,
  create: orderManifestCreateForm,
  update: orderManifestUpdateForm,
  hideGlobalFilter: true,
  addButtonText: 'Add Manifest Item',
  columns: [
    { type: ListViewColumnType.edit, accessor: 'edit' },
    { type: ListViewColumnType.delete, accessor: 'delete' },
    { Header: 'Item', id: 'productName', accessor: (row: any) => row?.dewarProductType?.description ?? row?.dewarProductType?.productNumber ?? row?.productTypeId },
    { Header: 'Qty Ship', accessor: 'qtyShipped' },
    { Header: 'Qty Return', accessor: 'qtyReturned' },
    { Header: 'Unit Price', id: 'unitPrice', accessor: (row: any) => money(row?.unitPrice) },
    {
      Header: 'Total',
      id: 'total',
      accessor: (row: any) => money(manifestLineTotal(row)),
      Footer: (info: any) => `Total: ${money((info?.rows ?? []).reduce((sum: number, r: any) => sum + manifestLineTotal(r.original), 0))}`,
    },
    { Header: 'Description', accessor: 'description' },
  ],
} as ListView;

export const OrderManifestListView = initListView(orderManifestsListView, orderManifestsDataSource);

// Inline Product Manifest grid for the Order edit form (replaces the modal-add
// CRUD listview). Bound to the `manifests` field, which the edit payload
// populates with only the user-entered "Product" lines; the server-generated
// fee lines are regenerated on save (RepriceDewarManifest). New rows POST with
// the order form (OrderSaveRequest.Manifests); the backend stamps orderId and
// computes the unit price, so the grid only edits product/qty/description.
export const orderManifestEditTable = {
  name: 'manifests',
  type: FormItemType.editTable,
  pk: 'id',
  showEmptyTable: true,
  emptyText: 'No product lines',
  addTemplate: () => ({ qtyShipped: 0, qtyReturned: 0 }),
  columns: [
    { type: ListViewColumnType.delete, accessor: 'delete' },
    {
      accessor: 'productTypeId',
      Header: 'Product',
      // All products (incl. Service Charge 110) so server-generated fee/"Other"
      // lines display their name; the add row still can't persist a fee (the save
      // path only honours user "Product" lines and regenerates fees).
      formItemProps: { name: 'productTypeId', type: FormItemType.select, searchable: true, required: true, options: manifestProductOptions },
    },
    { accessor: 'qtyShipped', Header: 'Qty Ship', formItemProps: { name: 'qtyShipped', number: true, defaultValue: 0, validation: positiveQtyValidation } },
    {
      accessor: 'qtyReturned',
      Header: 'Qty Return',
      formItemProps: {
        name: 'qtyReturned',
        number: true,
        defaultValue: 0,
        // Re-validate the Qty Ship cell so a return-only line clears the error.
        onChange: (_v: any, getValues: any, setValue: any, editTableProps: any) => {
          const i = editTableProps?.rowIdentity;
          setValue(`manifests[${i}][qtyShipped]`, getValues(`manifests[${i}][qtyShipped]`), { shouldValidate: true });
        },
      },
    },
    // Unit price: "Product" items are auto-priced server-side (read-only, matching
    // legacy which hid the override for Product); fee/Miscellaneous items keep a
    // manually editable unit price so the charge can be overridden (legacy
    // tbManifestOverrideUnitPrice). New rows have no product category yet, so they
    // stay editable until the row is saved and re-priced.
    {
      accessor: 'unitPrice',
      Header: 'Unit Price',
      formItemProps: {
        name: 'unitPrice',
        number: true,
        disabled: ({ editTableProps }: any) => {
          const row = editTableProps?.rowEntity;
          const loaded = row?.dewarProductType;
          // New row or product changed away from the loaded one → category is not
          // yet known, so allow the manual override; only a still-"Product" line
          // stays read-only (auto-priced server-side, matching legacy).
          if (!loaded || Number(row?.productTypeId) !== Number(loaded?.id)) return false;
          return (loaded?.productCategory ?? '') === 'Product';
        },
      },
    },
    {
      accessor: 'total',
      Header: 'Total',
      formItemProps: {
        name: 'total',
        type: FormItemType.labelField,
        computedValue: (getValues: any, editTableProps: any) => {
          const rows = getValues('manifests') || [];
          return money(manifestLineTotal(rows[editTableProps?.rowIdentity] ?? {}));
        },
      },
      Footer: (info: any) =>
        `Total: ${money((info?.rows ?? []).reduce((sum: number, r: any) => sum + manifestLineTotal(r.original), 0))}`,
    },
    { accessor: 'description', Header: 'Description', formItemProps: { name: 'description' } },
  ],
};
